import React, {CSSProperties, PropsWithChildren, useRef} from 'react';
import {useOperator} from "context/OperatorContext";
import * as Mapbox from "react-map-gl/mapbox";
import DrawControl, {DrawControlProps} from "../DrawControl";
import drawingStyles from "./drawingStyles";
import {Feature, FeatureCollection, Geometry} from "geojson";
import Map from "../Map";
import {featureCollection} from "../../services/geometry";
import MapboxDraw, {
  DrawCreateEvent,
  DrawDeleteEvent,
  DrawModeChangeEvent,
  DrawUpdateEvent
} from "@mapbox/mapbox-gl-draw";
import {getViewState} from "../../services/map";

// Types ----

type MapHeight = CSSProperties['height']

type DrawingTools = DrawControlProps['controls']

type MapboxViewState = Mapbox.MapProps['initialViewState'] | null

export type MapEditorProps = {
  height: MapHeight
  tools?: DrawingTools
  features?: FeatureCollection | null
  singleFeature?: boolean
  initialViewState?: MapboxViewState
  flyToGeometry?: Geometry
  onViewChanged?: (state: Mapbox.ViewState) => any
  onFeaturesCreated?: (features: Array<Feature>) => void
  onFeaturesUpdated?: (features: Array<Feature>) => void
  onFeaturesDeleted?: (features: Array<Feature>) => void
  onFeaturesChanged?: (allFeatures: Array<Feature>) => void
  showSearchBox?: boolean
}

// Helpers ----

function extractFeatureIds(collection: FeatureCollection): Array<string> {
  return collection
    .features
    .map(feature => String(feature.id))
    .filter(Boolean);
}

// Component ----

function MapEditor({
  height,
  tools,
  features,
  singleFeature,
  initialViewState,
  flyToGeometry,
  onViewChanged,
  onFeaturesCreated,
  onFeaturesUpdated,
  onFeaturesDeleted,
  onFeaturesChanged,
  showSearchBox,
  children
}: PropsWithChildren<MapEditorProps>) {
  const {settings} = useOperator();

  // Keep a reference to the drawing control
  const drawControl = useRef<MapboxDraw | null>(null);

  // Prepare the editable feature set
  const editableFeatures = features ?? featureCollection([]);

  // Load the editable features into the draw control
  if (drawControl.current) drawControl.current.set(editableFeatures);

  // Prepare the initial view state
  const startingViewState = initialViewState ?? getViewState(editableFeatures, settings?.audience_app.map_state);

  // Define event handlers
  function handleViewChanged(event: Mapbox.ViewStateChangeEvent) {
    if (onViewChanged) onViewChanged(event.viewState)
  }

  function handleLoaded(_event: object, control: MapboxDraw) {
    drawControl.current = control;
    control.set(editableFeatures);
  }

  function handleModeChanged(event: DrawModeChangeEvent, control: MapboxDraw) {
    if (!singleFeature) return;

    const drawingToolSelected = event.mode.startsWith('draw_');
    const featuresExist = control.getAll().features.length > 1;

    if (drawingToolSelected && featuresExist) {
      control.set(featureCollection(control.getAll().features.slice(-1)))
    }
  }

  function handleFeaturesCreated(event: DrawCreateEvent) {
    if (onFeaturesCreated) onFeaturesCreated(event.features);

    if (onFeaturesChanged && drawControl.current) onFeaturesChanged(drawControl.current.getAll().features);
  }

  function handleFeaturesUpdated(event: DrawUpdateEvent) {
    if (onFeaturesUpdated) onFeaturesUpdated(event.features);

    if (onFeaturesChanged && drawControl.current) onFeaturesChanged(drawControl.current.getAll().features);
  }

  function handleFeaturesDeleted(event: DrawDeleteEvent) {
    if (onFeaturesDeleted) onFeaturesDeleted(event.features);

    if (onFeaturesChanged && drawControl.current) onFeaturesChanged(drawControl.current.getAll().features);
  }

  // Render map with drawing tools
  return (
    <Map
      height={height}
      defaultMapStyle={settings?.audience_app.map_style ?? null}
      initialViewState={startingViewState}
      onViewChanged={handleViewChanged}
      hideFeatures={extractFeatureIds(editableFeatures)}
      hidePointsOfInterest
      flyToGeometry={flyToGeometry}
      flyToMode='bounds'
      showSearchBox={showSearchBox}
    >

      {/* Show static map features and other controls */}
      {children}

      {/* Show drawing control on top of other features */}
      <DrawControl
        controls={tools}
        styles={drawingStyles}
        displayControlsDefault={false}
        position="top-left"
        onLoad={handleLoaded}
        onModeChange={handleModeChanged}
        onCreate={handleFeaturesCreated}
        onUpdate={handleFeaturesUpdated}
        onDelete={handleFeaturesDeleted}
      />
    </Map>
  );
}

// Export ----

export default MapEditor;